import { propertyHooks } from '$src/lib/trpcHooks'
import { NumberInput, Select, Stack, Textarea, TextInput } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { AppModal } from '@markku/components'
import { propertyTypeEnum } from '@markku/db'
import { PROPERTY_TYPE_LABELS } from '@markku/utils'
import { useState } from 'react'

interface AddPropertyModalProps {
  opened: boolean
  onClose: () => void
  customerId?: number // Optional: if not provided, backend creates new customer
  onPropertyCreated?: (propertyId: number, propertyAddress: string) => void
}

export function AddPropertyModal({
  opened,
  onClose,
  customerId,
  onPropertyCreated,
}: AddPropertyModalProps) {
  const [street, setStreet] = useState('')
  const [city, setCity] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [propertyType, setPropertyType] = useState<string | null>(null)
  const [constructionYear, setConstructionYear] = useState<number | string>('')
  const [floorArea, setFloorArea] = useState<number | string>('')
  const [notes, setNotes] = useState('')

  const { mutateAsync: createProperty, isPending } = propertyHooks.useCreate()

  const handleSubmit = async () => {
    if (!street.trim() || !city.trim() || !propertyType) {
      notifications.show({
        title: 'Virhe',
        message: 'Täytä kaikki pakolliset kentät',
        color: 'red',
      })
      return
    }

    const result = await createProperty({
      data: {
        // Use provided customerId, or 0 as placeholder (backend will auto-create)
        customerId: customerId ?? 0,
        propertyType: propertyType as
          | 'detached_house'
          | 'semi_detached'
          | 'row_house'
          | 'apartment_building',
        constructionYear: constructionYear ? Number(constructionYear) : null,
        notes: notes.trim() || null,
        data: {
          floorArea: floorArea ? Number(floorArea) : null,
        },
        address: {
          street: street.trim(),
          city: city.trim(),
          postalCode: postalCode.trim() || null,
          country: null,
          latitude: null,
          longitude: null,
        },
      },
    })

    const fullAddress = `${street.trim()}, ${city.trim()}`

    if (onPropertyCreated) {
      onPropertyCreated(result.id, fullAddress)
    } else {
      onClose()
    }
  }

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title="Lisää kiinteistö"
      size="md"
      confirmProps={{
        label: 'Tallenna',
        onClick: handleSubmit,
        loading: isPending,
      }}
    >
      <Stack gap="md">
        <TextInput
          label="Katuosoite"
          placeholder="Esim. Esimerkkikatu 1"
          value={street}
          onChange={(e) => setStreet(e.currentTarget.value)}
          autoComplete="street-address"
          required
        />
        <TextInput
          label="Kaupunki"
          placeholder="Esim. Helsinki"
          value={city}
          onChange={(e) => setCity(e.currentTarget.value)}
          autoComplete="address-level2"
          required
        />
        <TextInput
          label="Postinumero"
          placeholder="Esim. 00100"
          value={postalCode}
          onChange={(e) => setPostalCode(e.currentTarget.value)}
          autoComplete="postal-code"
        />
        <Select
          label="Kiinteistötyyppi"
          placeholder="Valitse tyyppi"
          value={propertyType}
          onChange={setPropertyType}
          data={propertyTypeEnum.enumValues.map((value) => ({
            value,
            label: PROPERTY_TYPE_LABELS[value],
          }))}
          required
        />
        <NumberInput
          label="Rakennusvuosi"
          placeholder="Esim. 2010"
          value={constructionYear}
          onChange={setConstructionYear}
          min={1800}
          max={new Date().getFullYear() + 1}
        />
        <NumberInput
          label="Asuinpinta-ala"
          description="Kerrosala neliömetreinä (m²)"
          placeholder="Esim. 150"
          value={floorArea}
          onChange={setFloorArea}
          min={0}
          max={500}
        />
        <Textarea
          label="Muistiinpanot"
          placeholder="Vapaamuotoisia muistiinpanoja kiinteistöstä"
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          rows={3}
        />
      </Stack>
    </AppModal>
  )
}
